import { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { FileText, Mail, RefreshCw, CheckCircle } from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function VerifyEmail() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const email = searchParams.get("email") || "";
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [verified, setVerified] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [resending, setResending] = useState(false);
  const timerRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!email) navigate("/register", { replace: true });
    if (inputRef.current) inputRef.current.focus();
  }, [email, navigate]);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    timerRef.current = setTimeout(() => setResendCooldown(c => c - 1), 1000);
    return () => clearTimeout(timerRef.current);
  }, [resendCooldown]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (code.length !== 6) { setError("Please enter the full 6-digit code."); return; }
    setError("");
    setLoading(true);
    try {
      await axios.post(`${API}/auth/verify-email`, { email, code });
      setVerified(true);
      setTimeout(() => navigate("/login"), 2500);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Invalid code. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (resendCooldown > 0 || resending) return;
    setResending(true);
    setError("");
    try {
      await axios.post(`${API}/auth/resend-verification`, { email });
      setResendCooldown(60);
    } catch {
      setError("Could not resend. Please try again in a moment.");
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-screen flex" style={{ fontFamily: "IBM Plex Sans, sans-serif" }}>
      {/* Left panel */}
      <div className="hidden lg:flex lg:w-1/2 flex-col justify-between p-12"
        style={{ background: "linear-gradient(180deg, #6D28D9 0%, #2563EB 100%)" }}>
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 bg-white/20 rounded-lg flex items-center justify-center">
            <FileText className="w-5 h-5 text-white" />
          </div>
          <div>
            <p className="text-white font-bold text-sm" style={{ fontFamily: "Manrope" }}>Simple Sign In Tax</p>
            <p className="text-white/60 text-xs">for Childcare Centers</p>
          </div>
        </div>
        <div>
          <h2 className="text-4xl font-extrabold text-white mb-6 leading-tight" style={{ fontFamily: "Manrope" }}>
            One last step<br />to get started
          </h2>
          <p className="text-white/70 text-lg leading-relaxed">
            We sent a 6-digit code to your email. Enter it to activate your account and start your free trial.
          </p>
        </div>
        <p className="text-white/40 text-sm">© 2026 Simple Sign In Tax</p>
      </div>

      {/* Right panel */}
      <div className="flex-1 flex items-center justify-center p-8 bg-slate-50">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center"
              style={{ background: "linear-gradient(135deg, #6D28D9, #2563EB)" }}>
              <FileText className="w-4 h-4 text-white" />
            </div>
            <p className="font-bold text-slate-900 text-sm" style={{ fontFamily: "Manrope" }}>Simple Sign In Tax</p>
          </div>

          {verified ? (
            <div data-testid="verify-email-success" className="text-center py-8">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h2 className="text-2xl font-extrabold text-slate-900 mb-2" style={{ fontFamily: "Manrope" }}>Email verified!</h2>
              <p className="text-slate-500">Redirecting you to sign in…</p>
            </div>
          ) : (
            <>
              <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-6"
                style={{ background: "linear-gradient(135deg, #6D28D9, #2563EB)" }}>
                <Mail className="w-7 h-7 text-white" />
              </div>

              <h1 className="text-3xl font-extrabold text-slate-900 mb-2" style={{ fontFamily: "Manrope" }}>Check your inbox</h1>
              <p className="text-slate-500 mb-2">We sent a 6-digit code to:</p>
              <p data-testid="verify-email-address" className="font-semibold text-slate-900 mb-8 text-sm bg-slate-100 px-3 py-2 rounded-lg inline-block">{email}</p>

              {error && (
                <div data-testid="verify-email-error" className="bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 rounded-lg mb-6">
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Verification Code</label>
                  <input
                    ref={inputRef}
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={6}
                    value={code}
                    onChange={e => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                    placeholder="000000"
                    data-testid="verify-email-code-input"
                    className="w-full px-4 py-3 border border-slate-300 rounded-lg text-2xl font-bold tracking-[0.5em] text-center focus:outline-none focus:ring-2 focus:ring-[#6D28D9] focus:border-transparent bg-white"
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading || code.length !== 6}
                  data-testid="verify-email-submit"
                  className="w-full py-3 rounded-lg font-semibold text-white text-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ background: "linear-gradient(135deg, #6D28D9, #2563EB)" }}
                >
                  {loading ? "Verifying…" : "Verify Email"}
                </button>
              </form>

              <div className="mt-6 text-center">
                <p className="text-slate-500 text-sm mb-2">Didn't receive a code?</p>
                <button
                  onClick={handleResend}
                  disabled={resendCooldown > 0 || resending}
                  data-testid="verify-email-resend"
                  className="text-[#6D28D9] text-sm font-semibold hover:underline disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5 mx-auto"
                >
                  <RefreshCw className="w-3.5 h-3.5" />
                  {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : resending ? "Sending…" : "Resend code"}
                </button>
              </div>

              <p className="text-center text-slate-400 text-xs mt-8">
                Wrong email?{" "}
                <Link to="/register" className="text-[#6D28D9] hover:underline">Start over</Link>
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
